import React, { Component } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import DatePicker from "react-datepicker";
import moment from "moment";
import _ from "lodash";

import HistoryAPI from "../../../services/historyService";
import { ViewJob } from "../requestimates/displayJob";
import { convertFromUTC } from "../../../util/convert_timezone";
import { isMultiLocation, isAllLocations, getLocationName, getLocationBadgeColor } from "../../../util/locationUtils";
import "./history.css";

const DESKTOP_BREAKPOINT = 1024;

class History extends Component {
    state = {
        // Data
        jobs: [],

        // Filter options (loaded from API)
        categories: [],
        services: [],
        products: [],

        // Filter state
        startDate: moment().subtract(90, 'days').toDate(),
        endDate: new Date(),
        searchTerm: '',
        selectedSource: '',
        selectedStatus: '',
        selectedCategory: '',
        selectedService: '',
        selectedProduct: '',

        // UI state
        expandedRows: [],
        loading: true,
        error: null,
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT,

        // Pagination
        currentPage: 1,
        totalRecords: 0,
        pageSize: 25,

        // Detail modal
        showDetailModal: false,
        selectedJob: null,
        selectedJobServices: [],
        selectedJobNotes: [],
        selectedJobHistory: [],
        loadingJobDetails: false
    };

    // Debounced search
    debouncedSearch = _.debounce(() => {
        this.loadJobHistory();
    }, 500);

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);

        // Load filter options and job history in parallel
        await Promise.all([
            this.loadFilterOptions(),
            this.loadJobHistory()
        ]);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        this.debouncedSearch.cancel();
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    // ============================================================================
    // Data Loading
    // ============================================================================

    loadFilterOptions = async () => {
        try {
            const [categories, services, products] = await Promise.all([
                HistoryAPI.getCategories(this.props.entityId),
                HistoryAPI.getServices(this.props.entityId),
                HistoryAPI.getProducts(this.props.entityId)
            ]);

            this.setState({ categories, services, products });
        } catch (error) {
            console.error('Error loading filter options:', error);
        }
    };

    loadJobHistory = async () => {
        const {
            startDate, endDate, searchTerm, selectedSource,
            selectedStatus, selectedCategory, selectedService,
            selectedProduct, currentPage, pageSize
        } = this.state;

        this.setState({ loading: true, error: null });

        try {
            const filters = {
                startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
                endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
                search: searchTerm || undefined,
                source: selectedSource || undefined,
                status: selectedStatus || undefined,
                categoryId: selectedCategory || undefined,
                serviceId: selectedService || undefined,
                productId: selectedProduct || undefined,
                limit: pageSize,
                offset: (currentPage - 1) * pageSize
            };

            // Remove undefined values
            Object.keys(filters).forEach(key =>
                filters[key] === undefined && delete filters[key]
            );

            const locationId = this.props.selectedLocationId || null;
            const result = await HistoryAPI.getJobHistory(this.props.entityId, filters, locationId);

            this.setState({
                jobs: result.data || [],
                totalRecords: result.pagination?.total || 0,
                loading: false
            });
        } catch (error) {
            console.error('Error loading job history:', error);
            this.setState({
                error: 'Failed to load job history. Please try again.',
                loading: false,
                jobs: []
            });
        }
    };

    // ============================================================================
    // Filter Handlers
    // ============================================================================

    handleSearchChange = (e) => {
        const searchTerm = e.target.value;
        this.setState({ searchTerm, currentPage: 1 }, () => {
            this.debouncedSearch();
        });
    };

    handleStartDateChange = (date) => {
        this.setState({ startDate: date, currentPage: 1 }, () => {
            this.loadJobHistory();
        });
    };

    handleEndDateChange = (date) => {
        this.setState({ endDate: date, currentPage: 1 }, () => {
            this.loadJobHistory();
        });
    };

    handleSourceChange = (e) => {
        this.setState({ selectedSource: e.target.value, currentPage: 1 }, () => {
            this.loadJobHistory();
        });
    };

    handleStatusChange = (e) => {
        this.setState({ selectedStatus: e.target.value, currentPage: 1 }, () => {
            this.loadJobHistory();
        });
    };

    handleCategoryChange = (e) => {
        const categoryId = e.target.value;
        this.setState({
            selectedCategory: categoryId,
            selectedService: '',  // Reset service when category changes
            selectedProduct: '',  // Reset product when category changes
            currentPage: 1
        }, () => {
            this.loadJobHistory();
        });
    };

    handleServiceChange = (e) => {
        this.setState({ selectedService: e.target.value, currentPage: 1 }, () => {
            this.loadJobHistory();
        });
    };

    handleProductChange = (e) => {
        this.setState({ selectedProduct: e.target.value, currentPage: 1 }, () => {
            this.loadJobHistory();
        });
    };

    clearFilters = () => {
        this.setState({
            startDate: moment().subtract(90, 'days').toDate(),
            endDate: new Date(),
            searchTerm: '',
            selectedSource: '',
            selectedStatus: '',
            selectedCategory: '',
            selectedService: '',
            selectedProduct: '',
            currentPage: 1
        }, () => {
            this.loadJobHistory();
        });
    };

    // ============================================================================
    // Expandable Rows
    // ============================================================================

    handleToggleExpand = (requestId) => {
        const isExpanded = this.state.expandedRows.includes(requestId);

        if (isExpanded) {
            this.setState(prev => ({
                expandedRows: prev.expandedRows.filter(id => id !== requestId)
            }));
        } else {
            this.setState(prev => ({
                expandedRows: [...prev.expandedRows, requestId]
            }));
        }
    };

    // ============================================================================
    // View Job Modal
    // ============================================================================

    handleViewJob = async (job) => {
        this.setState({
            showDetailModal: true,
            selectedJob: job,
            loadingJobDetails: true
        });

        try {
            const details = await HistoryAPI.getJobDetails(job.request_id);
            // Use the full request record if available (same data shape as financials/requestimates)
            const fullRequest = details.request || job;
            this.setState({
                selectedJob: fullRequest,
                selectedJobServices: details.services,
                selectedJobNotes: details.notes,
                selectedJobHistory: details.history,
                loadingJobDetails: false
            });
        } catch (error) {
            console.error('Error loading job details:', error);
            this.setState({ loadingJobDetails: false });
        }
    };

    handleCloseModal = () => {
        this.setState({
            showDetailModal: false,
            selectedJob: null,
            selectedJobServices: [],
            selectedJobNotes: [],
            selectedJobHistory: []
        });
    };

    // ============================================================================
    // Rebook Job
    // ============================================================================

    handleRebookJob = (job) => {
        const duplicateData = {
            customer: job.customer?.id || job.dealer?.id,
            customerName: job.customer?.name || job.dealer?.name,
            customerEmail: job.customer?.email || job.dealer?.email,
            customerType: job.requestType === 'dealer' ? 'whole' : 'retail',
            vehicle: job.vehicle || {},
            requestId: job.request_id
        };
        this.props.history.push('/newestimate', { duplicateData });
    };

    // ============================================================================
    // Export
    // ============================================================================

    handleExportCsv = async () => {
        const {
            startDate, endDate, searchTerm, selectedSource,
            selectedStatus, selectedCategory, selectedService,
            selectedProduct
        } = this.state;

        try {
            const filters = {
                startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
                endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
                search: searchTerm || undefined,
                source: selectedSource || undefined,
                status: selectedStatus || undefined,
                categoryId: selectedCategory || undefined,
                serviceId: selectedService || undefined,
                productId: selectedProduct || undefined,
                limit: 5000 // Get all for export
            };

            Object.keys(filters).forEach(key =>
                filters[key] === undefined && delete filters[key]
            );

            const locationId = this.props.selectedLocationId || null;
            const result = await HistoryAPI.getJobHistory(this.props.entityId, filters, locationId);
            const jobs = result.data || [];

            // Convert to CSV with expanded service/material details
            // Each service becomes its own row for detailed reporting
            const multiLoc = isMultiLocation(this.props.locations);
            const headers = [
                'Date', 'Source', 'Customer/Dealer', 'Vehicle', 'VIN', 'Services Requested',
                'Category', 'Service', 'Material Label', 'Material Brand', 'Material Product', 'Material Qty',
                'Service Price', 'Job Total', 'Status', 'Invoice #',
                ...(multiLoc ? ['Location'] : [])
            ];

            const rows = [];
            jobs.forEach(job => {
                const baseRow = {
                    date: moment(job.completed_date || job.service_date).format('YYYY-MM-DD'),
                    source: this.getSourceLabel(job),
                    customerDealer: job.customer?.name || job.dealer?.name || '-',
                    vehicle: job.vehicle ? `${job.vehicle.year} ${job.vehicle.make} ${job.vehicle.model}` : '-',
                    vin: job.vehicle?.vin || '-',
                    serviceRequested: job.service_requested || job.serviceRequested || '-',
                    jobTotal: job.requestTotal || 0,
                    status: _.capitalize(job.currentState || ''),
                    invoice: job.invoice_number || job.invoice_id || '-',
                    location: job.location_name || '-'
                };
                const locationCol = multiLoc ? [baseRow.location] : [];

                const services = job.services || [];
                if (services.length === 0) {
                    rows.push([
                        baseRow.date, baseRow.source, baseRow.customerDealer, baseRow.vehicle, baseRow.vin,
                        baseRow.serviceRequested, '-', '-', '-', '-', '-', '-', '-',
                        baseRow.jobTotal, baseRow.status, baseRow.invoice, ...locationCol
                    ]);
                } else {
                    services.forEach(svc => {
                        const materials = svc.materials || [];
                        if (materials.length === 0) {
                            rows.push([
                                baseRow.date, baseRow.source, baseRow.customerDealer, baseRow.vehicle, baseRow.vin,
                                baseRow.serviceRequested, svc.category_name || '-', svc.service_name || '-',
                                '-', '-', '-', '-', svc.price || '-',
                                baseRow.jobTotal, baseRow.status, baseRow.invoice, ...locationCol
                            ]);
                        } else {
                            materials.forEach(mat => {
                                rows.push([
                                    baseRow.date, baseRow.source, baseRow.customerDealer, baseRow.vehicle, baseRow.vin,
                                    baseRow.serviceRequested, svc.category_name || '-', svc.service_name || '-',
                                    (mat.label && mat.label !== 'All' ? mat.label : '-'), mat.brand || '-', mat.product_name || '-', mat.quantity_used || mat.quantity || 1, svc.price || '-',
                                    baseRow.jobTotal, baseRow.status, baseRow.invoice, ...locationCol
                                ]);
                            });
                        }
                    });
                }
            });

            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            ].join('\n');

            // Download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `job_history_${moment().format('YYYY-MM-DD')}.csv`;
            link.click();
        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Failed to export. Please try again.');
        }
    };

    // ============================================================================
    // Helpers
    // ============================================================================

    getSourceLabel = (job) => {
        // Use the new source field from backend if available
        if (job.source) {
            // Map raw source values to display labels (matches Source filter dropdown)
            const sourceMap = {
                'dealer': 'Dealer', 'dealer_direct': 'Dealer', 'direct': 'Direct',
                'specialist': 'Specialist', 'google': 'Google', 'facebook': 'Facebook',
                'organic': 'Organic', 'returning': 'Returning', 'other': 'Other'
            };
            return sourceMap[job.source.toLowerCase()] || _.capitalize(job.source);
        }
        // Fallback for older records without source field
        if (job.referralType) return 'Referral';
        if (job.requestType === 'dealer') return 'Dealer';
        return 'Retail';
    };

    getSourceBadgeClass = (source) => {
        const sourceLower = (source || '').toLowerCase();
        switch (sourceLower) {
            case 'dealer':
            case 'direct':
                return 'badge-info';
            case 'google':
            case 'facebook':
                return 'badge-primary';
            case 'specialist':
            case 'referral':
                return 'badge-success';
            case 'returning':
                return 'badge-warning';
            case 'organic':
            case 'retail':
                return 'badge-secondary';
            default:
                return 'badge-light';
        }
    };

    formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toFixed(2)}`;
    };

    getFilteredServices = () => {
        const { services, selectedCategory } = this.state;
        if (!selectedCategory) return services;
        return services.filter(s =>
            String(s.category_id) === String(selectedCategory)
        );
    };

    getFilteredProducts = () => {
        const { products, selectedCategory } = this.state;
        if (!selectedCategory) return products;
        return products.filter(p =>
            String(p.category_id) === String(selectedCategory)
        );
    };

    // ============================================================================
    // Render
    // ============================================================================

    renderFilters() {
        const {
            startDate, endDate, searchTerm, selectedSource,
            selectedStatus, selectedCategory, selectedService,
            selectedProduct, categories
        } = this.state;

        const filteredServices = this.getFilteredServices();
        const filteredProducts = this.getFilteredProducts();

        return (
            <div className="history-filters">
                {/* Row 1: Date Range and Search */}
                <div className="filter-row">
                    <div className="filter-group date-range">
                        <label>Date Range</label>
                        <div className="date-pickers">
                            <DatePicker
                                selected={startDate}
                                onChange={this.handleStartDateChange}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                dateFormat="MMM d, yyyy"
                                className="form-control"
                                placeholderText="From"
                            />
                            <span className="date-separator">to</span>
                            <DatePicker
                                selected={endDate}
                                onChange={this.handleEndDateChange}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                dateFormat="MMM d, yyyy"
                                className="form-control"
                                placeholderText="To"
                            />
                        </div>
                    </div>

                    <div className="filter-group search-box">
                        <label>Search</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Customer, dealer, VIN, vehicle..."
                            value={searchTerm}
                            onChange={this.handleSearchChange}
                        />
                    </div>
                </div>

                {/* Row 2: Dropdowns */}
                <div className="filter-row">
                    <div className="filter-group">
                        <label>Source</label>
                        <select
                            className="form-control"
                            value={selectedSource}
                            onChange={this.handleSourceChange}
                        >
                            <option value="">All Sources</option>
                            <option value="dealer">Dealer</option>
                            <option value="dealer_direct">Direct</option>
                            <option value="specialist">Specialist</option>
                            <option value="google">Google</option>
                            <option value="facebook">Facebook</option>
                            <option value="organic">Organic</option>
                            <option value="returning">Returning</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Status</label>
                        <select
                            className="form-control"
                            value={selectedStatus}
                            onChange={this.handleStatusChange}
                        >
                            <option value="">All Statuses</option>
                            <option value="collected">Collected</option>
                            <option value="invoiced">Invoiced</option>
                            <option value="paid">Paid</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Category</label>
                        <select
                            className="form-control"
                            value={selectedCategory}
                            onChange={this.handleCategoryChange}
                        >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat.category_id} value={cat.category_id}>
                                    {cat.category_name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Row 3: Cascading filters */}
                <div className="filter-row">
                    <div className="filter-group">
                        <label>Service</label>
                        <select
                            className="form-control"
                            value={selectedService}
                            onChange={this.handleServiceChange}
                        >
                            <option value="">All Services</option>
                            {filteredServices.map(svc => (
                                <option key={svc.service_id} value={svc.service_id}>
                                    {svc.service_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Material</label>
                        <select
                            className="form-control"
                            value={selectedProduct}
                            onChange={this.handleProductChange}
                        >
                            <option value="">All Materials</option>
                            {filteredProducts.map(prod => (
                                <option key={prod.product_id} value={prod.product_id}>
                                    {prod.brand} - {prod.product_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group filter-actions">
                        <label>&nbsp;</label>
                        <div className="btn-group">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={this.clearFilters}
                            >
                                Clear
                            </button>
                            <button
                                className="btn btn-outline-primary"
                                onClick={this.handleExportCsv}
                            >
                                <i className="fa-sharp fa-regular fa-download"></i> Export CSV
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Calculate actual duration from started_at/finished_at timestamps
    formatActualDuration = (svc) => {
        const startedAt = svc.started_at;
        const finishedAt = svc.finished_at;

        if (!startedAt || !finishedAt) {
            return '-';
        }

        const start = moment(startedAt);
        const end = moment(finishedAt);
        const diffMinutes = end.diff(start, 'minutes');

        if (diffMinutes < 0) return '-';
        if (diffMinutes < 60) return `${diffMinutes}m`;

        const hours = Math.floor(diffMinutes / 60);
        const mins = diffMinutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    renderExpandedRow = (job) => {
        const services = job.services || [];

        if (services.length === 0) {
            return (
                <div className="expanded-row-content">
                    <p className="text-muted">No service details available</p>
                </div>
            );
        }

        return (
            <div className="expanded-row-content">
                <div className="services-header">
                    <strong>Services ({services.length})</strong>
                </div>
                <table className="services-table">
                    <thead>
                        <tr>
                            <th>Service</th>
                            <th>Materials</th>
                            <th>Amount</th>
                            <th>Technician</th>
                            <th>Actual Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {services.map((svc, idx) => (
                            <tr key={idx}>
                                <td>{svc.category_name ? `${svc.category_name} - ` : ''}{svc.service_name || '-'}</td>
                                <td>
                                    {svc.materials && svc.materials.length > 0
                                        ? svc.materials.map((m, i) => (
                                            <div key={i}>
                                                {m.label && m.label !== 'All' ? `${m.label}: ` : ''}
                                                {m.brand} - {m.product_name}
                                                {m.quantity_used ? ` (${m.quantity_used}${m.unit_code ? ' ' + m.unit_code : ''})` : ''}
                                            </div>
                                          ))
                                        : '-'
                                    }
                                </td>
                                <td>{this.formatCurrency(svc.price)}</td>
                                <td>{svc.technician_name || svc.finished_by_name || '-'}</td>
                                <td>{this.formatActualDuration(svc)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="mt-2 text-right">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => this.handleRebookJob(job)}
                        title="Rebook this job with current catalog prices"
                    >
                        <i className="fa-sharp fa-regular fa-redo mr-1"></i>Rebook
                    </button>
                </div>
            </div>
        );
    };

    renderTable() {
        const { jobs, loading, totalRecords, currentPage, pageSize } = this.state;

        if (loading) {
            return (
                <div className="text-center py-5">
                    <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-3">Loading job history...</p>
                </div>
            );
        }

        if (jobs.length === 0) {
            return (
                <div className="text-center py-5">
                    <i className="fa-sharp fa-regular fa-search fa-2x text-muted"></i>
                    <p className="mt-3 text-muted">No jobs found matching your filters</p>
                </div>
            );
        }

        // Include _expanded flag so react-bootstrap-table re-renders the cell when expand state changes
        const tableData = jobs.map(j => ({
            ...j,
            _expanded: this.state.expandedRows.includes(j.request_id)
        }));

        const showLocationCol = isMultiLocation(this.props.locations) && isAllLocations(this.props.selectedLocationId);

        const columns = [
            {
                dataField: '_expanded',
                text: '',
                formatter: (cell, row) => {
                    const hasServices = row.services && row.services.length > 0;
                    return hasServices ? (
                        <span
                            className="expand-toggle"
                            onClick={(e) => {
                                e.stopPropagation();
                                this.handleToggleExpand(row.request_id);
                            }}
                        >
                            {cell ? '▼' : '▶'}
                        </span>
                    ) : null;
                },
                headerStyle: { width: '40px' }
            },
            {
                dataField: 'completed_date',
                text: 'Date',
                formatter: (cell, row) => convertFromUTC(cell || row.service_date).format('MMM D, YYYY'),
                headerStyle: { width: '120px' }
            },
            {
                dataField: 'source',
                text: 'Source',
                formatter: (cell, row) => {
                    const label = this.getSourceLabel(row);
                    const badgeClass = this.getSourceBadgeClass(label);
                    return <span className={`badge ${badgeClass}`}>{label}</span>;
                },
                headerStyle: { width: '100px' }
            },
            ...(showLocationCol ? [{
                dataField: 'location_name',
                text: 'Location',
                formatter: (cell, row) => {
                    const locId = row.location_id || row.locationId;
                    if (!locId) return '-';
                    const locName = row.locationName || row.location_name || getLocationName((this.props.locations || []).find(l => String(l.location_id) === String(locId)));
                    return (
                        <span className="badge" style={{
                            backgroundColor: getLocationBadgeColor(locId, this.props.locations),
                            color: '#fff',
                            fontSize: '0.65rem'
                        }}>
                            {locName}
                        </span>
                    );
                },
                headerStyle: { width: '120px' }
            }] : []),
            {
                dataField: 'customer_dealer',
                text: 'Customer/Dealer',
                formatter: (cell, row) => row.customer?.name || row.dealer?.name || '-'
            },
            {
                dataField: 'vehicle',
                text: 'Vehicle',
                formatter: (cell, row) => {
                    if (!row.vehicle) return '-';
                    const vehicleName = `${row.vehicle.year} ${row.vehicle.make} ${row.vehicle.model}`;
                    return (
                        <a
                            href="#/"
                            onClick={(e) => {
                                e.preventDefault();
                                this.handleViewJob(row);
                            }}
                        >
                            {vehicleName}
                        </a>
                    );
                }
            },
            {
                dataField: 'service_requested',
                text: 'Services',
                formatter: (cell, row) => {
                    // Use the service_requested composite field from the request
                    // Check both snake_case and camelCase versions
                    return row.service_requested || row.serviceRequested || '-';
                }
            },
            {
                dataField: 'materials_count',
                text: 'Materials',
                formatter: (cell, row) => {
                    if (!row.services) return '-';
                    const materials = row.services.flatMap(s => s.materials || []);
                    if (materials.length === 0) return '-';
                    return `${materials.length} recorded`;
                },
                headerStyle: { width: '110px' }
            },
            {
                dataField: 'requestTotal',
                text: 'Amount',
                formatter: (cell) => this.formatCurrency(cell),
                headerStyle: { width: '100px' }
            },
            {
                dataField: 'currentState',
                text: 'Status',
                formatter: (cell) => {
                    const badgeClass = cell === 'paid' ? 'badge-success'
                        : cell === 'invoiced' ? 'badge-warning'
                        : 'badge-info';
                    return <span className={`badge ${badgeClass}`}>{_.capitalize(cell)}</span>;
                },
                headerStyle: { width: '100px' }
            }
        ];

        const expandRow = {
            renderer: this.renderExpandedRow,
            expanded: this.state.expandedRows,
            showExpandColumn: false,
            expandByColumnOnly: true,
            nonExpandable: jobs.filter(j => !j.services || j.services.length === 0).map(j => j.request_id)
        };

        const totalPages = Math.ceil(totalRecords / pageSize);

        // Row styling based on request type (dealer = orange, retail = blue)
        const rowClasses = (row) => {
            const type = (row.requestType || '').toLowerCase();
            return type === 'dealer' ? 'source-dealer' : 'source-retail';
        };

        return (
            <div>
                <BootstrapTable
                    keyField="request_id"
                    data={tableData}
                    columns={columns}
                    expandRow={expandRow}
                    rowClasses={rowClasses}
                    bordered={false}
                    hover
                    wrapperClasses="history-table-wrapper"
                />

                {/* Manual Pagination */}
                {totalRecords > 0 && (
                    <div className="history-pagination">
                        <div className="pagination-info">
                            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
                        </div>
                        <div className="pagination-controls">
                            <select
                                className="form-control form-control-sm page-size-select"
                                value={pageSize}
                                onChange={(e) => this.setState({ pageSize: parseInt(e.target.value), currentPage: 1 }, () => this.loadJobHistory())}
                            >
                                <option value="25">25 per page</option>
                                <option value="50">50 per page</option>
                                <option value="100">100 per page</option>
                            </select>
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => this.setState({ currentPage: 1 }, () => this.loadJobHistory())}
                                            disabled={currentPage === 1}
                                        >
                                            First
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => this.setState({ currentPage: currentPage - 1 }, () => this.loadJobHistory())}
                                            disabled={currentPage === 1}
                                        >
                                            Prev
                                        </button>
                                    </li>
                                    <li className="page-item active">
                                        <span className="page-link">
                                            {currentPage} / {totalPages}
                                        </span>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => this.setState({ currentPage: currentPage + 1 }, () => this.loadJobHistory())}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => this.setState({ currentPage: totalPages }, () => this.loadJobHistory())}
                                            disabled={currentPage === totalPages}
                                        >
                                            Last
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    renderModal() {
        const {
            showDetailModal, selectedJob, selectedJobServices,
            selectedJobNotes, selectedJobHistory
        } = this.state;

        if (!selectedJob) return null;

        // Pass the full request record directly (same shape as financials/requestimates)
        return (
            <ViewJob
                show={showDetailModal}
                onHide={this.handleCloseModal}
                request={selectedJob}
                services={selectedJobServices}
                notes={selectedJobNotes}
                history={selectedJobHistory}
                userType={this.props.userType}
            />
        );
    }

    render() {
        const { error, totalRecords } = this.state;

        return (
            <div className="history-container">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                        <button
                            type="button"
                            className="btn btn-link"
                            onClick={this.loadJobHistory}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {this.renderFilters()}

                <div className="results-count">
                    {totalRecords} {totalRecords === 1 ? 'job' : 'jobs'} found
                </div>

                <div className="history-table-container">
                    {this.renderTable()}
                </div>

                {this.renderModal()}
            </div>
        );
    }
}

export default History;
